import {createProgram} from "../primitiveHelpers.js";
import {primitiveFragShader, primitiveVertShader} from "../shaders/primitiveShader.js";
import mapboxgl from "mapbox-gl";
import * as THREE from "three";
import { getScalingFactor } from "../helpers.js";
import Stats from "three/addons/libs/stats.module.js";
import {textureFragShader, textureVertShader} from "../shaders/texturePointShader.js";


let program;
let vbo;
let pos = [];
let plateCreated = false;
let statsPanels;
let texture = null;

let rotation = 90;

const anchor = [0, 0];
const plateSize = 1_000_000;
const earthRadius = 1303.797293808806;

const earthsNipple = [
  [-6.963272,-0.838364],
  [-1.737213,2.887495],
  [5.339355,1.649781],
  [3.542404,-5.632386],
  [-3.712692,-4.655133]
];

function loadTexture(gl, url) {
  const createdTexture = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, createdTexture);

  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    1,
    1,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    new Uint8Array([255, 255, 255, 255])
  );

  const img = new Image();
  img.onload = () => {
    gl.bindTexture(gl.TEXTURE_2D, createdTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  };
  img.src = url;

  return createdTexture;
}

export const texturePointLayer = {
  id: 'texture-point-layer',
  type: 'custom',
  onAdd: (_, gl) => {

    const stats1 = new Stats();
    stats1.showPanel(0);
    document.body.appendChild(stats1.dom);

    const stats2 = new Stats();
    stats2.showPanel(1);
    stats2.dom.style.left = '80px';
    document.body.appendChild(stats2.dom);

    const stats3 = new Stats();
    stats3.showPanel(2);
    stats3.dom.style.left = '160px';
    document.body.appendChild(stats3.dom);
    statsPanels = [stats1, stats2, stats3];

    vbo = gl.createBuffer();
    program = createProgram(gl, textureVertShader, textureFragShader);

    const altitude = 0;

    for (
      let i = 0;
      i < earthsNipple.length;
      i++
    ) {

      const lon = earthsNipple[i][0];
      const lat = earthsNipple[i][1];

      if (lon && lat) {
        const merc = mapboxgl.MercatorCoordinate.fromLngLat(
          [lon, lat],
          altitude,
        );

        pos.push(merc.x, merc.y, merc.z);
      }
    }

    updateVboAndActivateAttrib(
      gl,
      program,
      vbo,
      pos,
      'position',
    );

    texture = loadTexture(gl, "/public/textures/triangle.png");


    gl.useProgram(program);
    setUniform(gl, program, "u_texture", 0);
  },
  render: (
    gl,
    projectionMatrix,
    projection,
    globeToMercMatrix,
    transition,
    centerInMercator,
    pixelsPerMeterRatio,
  ) => {
    statsPanels.forEach(stats => stats.begin());

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, vbo);

    if (texture) {
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(
        gl.getUniformLocation(program, 'u_texture'),
        0,
      );
    }

    if (rotation === 360) {
      rotation = 0;
    } else {
      rotation += 1;
    }

    setRotationUniform(gl, program, degreesToRadians(rotation))


    if (projection && projection.name === 'globe') {
      if (!plateCreated) {

        const plate = new mapboxgl.LngLat(anchor[0], anchor[1]).toBounds(plateSize);

        const nw_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getNorthWest(), 0);
        const sw_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getSouthWest(), 0);
        const ne_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getNorthEast(), 0);
        const se_merc = mapboxgl.MercatorCoordinate.fromLngLat(plate.getSouthEast(), 0);


        setUniform(gl, program, "u_plate_tl_up", new THREE.Vector3().fromArray(plate.getNorthWest().toEcef(0)));
        setUniform(gl, program, "u_plate_br_up", new THREE.Vector3().fromArray(plate.getSouthWest().toEcef(0)));
        setUniform(gl, program, "u_plate_tr_up", new THREE.Vector3().fromArray(plate.getNorthEast().toEcef(0)));
        setUniform(gl, program, "u_plate_bl_up", new THREE.Vector3().fromArray(plate.getSouthEast().toEcef(0)));

        setUniform(gl, program, "u_plate_tl", new THREE.Vector3(nw_merc.x, nw_merc.y, nw_merc.z));
        // setUniform(gl, program, "u_plate_bl", new THREE.Vector3(sw_merc.x, sw_merc.y, sw_merc.z));
        setUniform(gl, program, "u_plate_tr", new THREE.Vector3(ne_merc.x, ne_merc.y, ne_merc.z));
        // setUniform(gl, program, "u_plate_br", new THREE.Vector3(se_merc.x, se_merc.y, se_merc.z));

        setUniform(gl, program, "u_plate_width", nw_merc.x - ne_merc.x)
        setUniform(gl, program, "u_plate_height", sw_merc.y - nw_merc.y)
        setUniform(gl, program, "u_scale_factor", getScalingFactor(anchor[1]))
        setUniform(gl, program, "u_plate_up_scale", earthRadius)

        plateCreated = true;
      }


      activateAttrib(gl, program, 'position');

      setUniform(gl, program, "u_matrix_globe_merc", new THREE.Matrix4().fromArray(globeToMercMatrix));
      setUniform(gl, program, "projectionMatrix", new THREE.Matrix4().fromArray(projectionMatrix));

      setUniform(gl, program, "u_zoom_transition", transition);
      setUniform(gl, program, "u_pixelsPerMeterRatio", pixelsPerMeterRatio);
      setUniform(gl, program, "u_centerInMerc", new THREE.Vector2().fromArray(centerInMercator));

      // setUniform(gl, program, "u_point_size", 5.0);

      gl.drawArrays(gl.POINTS, 0, pos.length / 3);
    } else {
      activateAttrib(gl, program, 'position');
      // setUniform(gl, program, "u_point_size", 5.0);

      setUniform(gl, program, "projectionMatrix", new THREE.Matrix4().fromArray(projectionMatrix));
      setUniform(gl, program, "u_zoom_transition", 1);
      gl.drawArrays(gl.POINTS, 0, pos.length / 3);
    }


    statsPanels.forEach(stats => stats.end());
  }



}


function activateAttrib(
  gl,
  program,
  attribName,
) {
  const attribLoc = gl.getAttribLocation(program, attribName);
  gl.vertexAttribPointer(attribLoc, 3, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(attribLoc);
}

function setUniform(gl, program, uniformName, value) {
  const location = gl.getUniformLocation(program, uniformName);
  if (!location) {
    console.warn(`Uniform ${uniformName} not found`);
    return;
  }

  if (value instanceof THREE.Matrix4) {
    gl.uniformMatrix4fv(location, false, value.elements);
  } else if (value instanceof THREE.Vector3) {
    gl.uniform3fv(location, value.toArray());
  } else if (value instanceof THREE.Vector2) {
    gl.uniform2fv(location, value.toArray());
  } else if (typeof value === "number") {
    gl.uniform1f(location, value);
  } else {
    console.error(`Incorrect uniform type for ${uniformName}`);
  }
}


function updateVboAndActivateAttrib(
  gl,
  prog,
  vbo,
  data,
  attribName,
) {
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.DYNAMIC_DRAW);
  const attribLoc = gl.getAttribLocation(prog, attribName);
  gl.vertexAttribPointer(attribLoc, 3, gl.FLOAT, false, 0, 0);
  gl.enableVertexAttribArray(attribLoc);
}

function setRotationUniform(gl, program, angle) {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);

  const rotationMatrix = new Float32Array([
    cosA, -sinA,
    sinA,  cosA
  ]);

  const location = gl.getUniformLocation(program, "u_rotationMatrix");
  gl.uniformMatrix2fv(location, false, rotationMatrix);
}

function degreesToRadians(deg) {
  return deg * (Math.PI / 180);
}
